
import { Database } from './types';

// Helper to get data from localStorage
const getStorage = (key: string) => {
    try {
        const item = localStorage.getItem(`mock_${key}`);
        return item ? JSON.parse(item) : [];
    } catch {
        return [];
    }
};

// Helper to set data to localStorage
const setStorage = (key: string, data: any[]) => {
    localStorage.setItem(`mock_${key}`, JSON.stringify(data));
};

// Initial Seed Data
const seedData = () => {
    if (!localStorage.getItem('mock_initialized')) {
        console.log('Seeding mock data...');

        // Seed Users (Auth)
        const adminUser = {
            id: 'admin-user-id',
            email: 'admin@slim.com',
            role: 'authenticated',
            created_at: new Date().toISOString(),
        };
        localStorage.setItem('mock_auth_user', JSON.stringify(adminUser));

        // Seed User Roles
        setStorage('user_roles', [
            { id: 'role-1', user_id: 'admin-user-id', role: 'admin', created_at: new Date().toISOString() }
        ]);

        // Seed Products
        setStorage('products', [
            {
                id: 'prod-1',
                name: 'مشد الخصر الأصلي',
                description: 'مشد عالي الجودة لنحت الخصر',
                price: 450,
                original_price: 600,
                image_url: 'https://images.unsplash.com/photo-1599058945522-28d584b6f0ff?w=800&auto=format&fit=crop&q=60',
                status: 'active',
                slug: 'waist-trainer',
                stock_quantity: 100,
                created_at: new Date().toISOString()
            }
        ]);

        // Seed Home Settings (Required for Home Builder)
        setStorage('home_settings', [
            {
                id: 'home-settings-1',
                hero_product_id: 'prod-1',
                show_hero_section: true,
                show_products_section: true,
                products_per_row: 4,
                header_text: 'مرحباً بك في متجرنا',
                footer_text: 'جميع الحقوق محفوظة',
                sections_order: ["banner", "hero", "products"],
                page_settings: {
                    fontFamily: "Cairo",
                    buttonStyle: "rounded",
                    primaryColor: "#10b981",
                    secondaryColor: "#059669",
                    backgroundColor: "#ffffff"
                },
                created_at: new Date().toISOString()
            }
        ]);

        // Seed Home Page Elements
        setStorage('home_page_elements', []);

        // Seed Custom Landing Pages
        setStorage('custom_landing_pages', []);
        setStorage('landing_page_elements', []);

        // Seed Upsell/Downsell
        setStorage('upsell_offers', []);
        setStorage('downsell_offers', []);

        // Seed AB Testing
        setStorage('experiments', []);
        setStorage('experiment_variants', []);
        setStorage('experiment_results', []);

        // Seed Analytics
        setStorage('analytics_events', []);
        setStorage('analytics_snapshots', []);
        setStorage('page_views', []);
        setStorage('user_sessions', []);
        setStorage('heatmap_data', []);

        // Seed Funnels
        setStorage('funnels', []);
        setStorage('funnel_steps', []);
        setStorage('funnel_connections', []);
        setStorage('funnel_analytics', []);

        // Seed Site Settings
        setStorage('site_settings', [
            {
                id: 'site-settings-1',
                announcement_text: 'شحن مجاني للطلبات فوق 600 جنيه',
                announcement_enabled: true,
                free_shipping_threshold: 600,
                shipping_cost: 50,
                created_at: new Date().toISOString()
            }
        ]);

        // Seed Brand Settings
        setStorage('brand_settings', [
            {
                id: 'brand-settings-1',
                store_name: 'المتجر',
                primary_color: '#10b981',
                secondary_color: '#059669',
                accent_color: '#34d399',
                created_at: new Date().toISOString()
            }
        ]);

        localStorage.setItem('mock_initialized', 'true');
    }
};

seedData();

class MockQueryBuilder {
    table: string;
    filters: any[];
    _select: string | null;
    _order: any;
    _limit: number | null;
    _single: boolean;
    _maybeSingle: boolean;
    _operation: 'select' | 'insert' | 'update' | 'delete';
    _data: any;

    constructor(table: string) {
        this.table = table;
        this.filters = [];
        this._select = null;
        this._order = null;
        this._limit = null;
        this._single = false;
        this._maybeSingle = false;
        this._operation = 'select';
        this._data = null;
    }

    select(columns = '*') {
        this._select = columns;
        // If we are already doing an insert/update, select just modifies the return
        return this;
    }

    insert(data: any) {
        this._operation = 'insert';
        this._data = data;
        return this;
    }

    update(data: any) {
        this._operation = 'update';
        this._data = data;
        return this;
    }

    delete() {
        this._operation = 'delete';
        return this;
    }

    eq(column: string, value: any) {
        this.filters.push({ type: 'eq', column, value });
        return this;
    }

    in(column: string, values: any[]) {
        this.filters.push({ type: 'in', column, values });
        return this;
    }

    gte(column: string, value: any) {
        this.filters.push({ type: 'gte', column, value });
        return this;
    }

    neq(column: string, value: any) {
        this.filters.push({ type: 'neq', column, value });
        return this;
    }

    order(column: string, { ascending = true } = {}) {
        this._order = { column, ascending };
        return this;
    }

    limit(count: number) {
        this._limit = count;
        return this;
    }

    single() {
        this._single = true;
        return this;
    }

    maybeSingle() {
        this._maybeSingle = true;
        return this;
    }

    async then(resolve: (res: any) => void, reject: (err: any) => void) {
        try {
            let resultData: any = null;
            let error: any = null;
            let count: number | null = null;

            if (this._operation === 'insert') {
                const currentData = getStorage(this.table);
                const newData = Array.isArray(this._data) ? this._data : [this._data];

                const inserted = newData.map((item: any) => ({
                    ...item,
                    id: item.id || crypto.randomUUID(),
                    created_at: item.created_at || new Date().toISOString(),
                    updated_at: item.updated_at || new Date().toISOString(),
                }));

                setStorage(this.table, [...currentData, ...inserted]);
                resultData = inserted;
            } else if (this._operation === 'update') {
                const currentData = getStorage(this.table);
                let updatedCount = 0;

                const updatedData = currentData.map((row: any) => {
                    let match = true;
                    for (const filter of this.filters) {
                        if (filter.type === 'eq' && row[filter.column] !== filter.value) match = false;
                        if (filter.type === 'neq' && row[filter.column] === filter.value) match = false;
                        // Add other filter types if needed for update
                    }

                    if (match) {
                        updatedCount++;
                        return { ...row, ...this._data, updated_at: new Date().toISOString() };
                    }
                    return row;
                });

                setStorage(this.table, updatedData);
                resultData = this._data;
                if (this._select) {
                    resultData = updatedData.filter((row: any) => {
                        let match = true;
                        for (const filter of this.filters) {
                            if (filter.type === 'eq' && row[filter.column] !== filter.value) match = false;
                            if (filter.type === 'neq' && row[filter.column] === filter.value) match = false;
                        }
                        return match;
                    });
                }
                count = updatedCount;
            } else if (this._operation === 'delete') {
                const currentData = getStorage(this.table);
                const keptData = currentData.filter((row: any) => {
                    let match = true;
                    for (const filter of this.filters) {
                        if (filter.type === 'eq' && row[filter.column] !== filter.value) {
                            match = false;
                            break;
                        }
                        if (filter.type === 'neq' && row[filter.column] === filter.value) {
                            match = false;
                            break;
                        }
                        if (filter.type === 'in' && !filter.values.includes(row[filter.column])) {
                            match = false;
                            break;
                        }
                        if (filter.type === 'gte' && row[filter.column] < filter.value) {
                            match = false;
                            break;
                        }
                    }
                    // If match is true, it means the row matches the criteria to be DELETED.
                    // So we want to KEEP it if match is false.
                    return !match;
                });

                setStorage(this.table, keptData);
                resultData = null;
            } else {
                // SELECT
                let data = getStorage(this.table);

                // Apply Filters
                for (const filter of this.filters) {
                    if (filter.type === 'eq') {
                        data = data.filter((row: any) => row[filter.column] === filter.value);
                    } else if (filter.type === 'neq') {
                        data = data.filter((row: any) => row[filter.column] !== filter.value);
                    } else if (filter.type === 'in') {
                        data = data.filter((row: any) => filter.values.includes(row[filter.column]));
                    } else if (filter.type === 'gte') {
                        data = data.filter((row: any) => row[filter.column] >= filter.value);
                    }
                }

                // Apply Order
                if (this._order) {
                    data.sort((a: any, b: any) => {
                        if (a[this._order.column] < b[this._order.column]) return this._order.ascending ? -1 : 1;
                        if (a[this._order.column] > b[this._order.column]) return this._order.ascending ? 1 : -1;
                        return 0;
                    });
                }

                // Apply Limit
                if (this._limit) {
                    data = data.slice(0, this._limit);
                }

                resultData = data;
            }

            // Handle Single/MaybeSingle for Select or Insert-returning-rows
            if (this._single) {
                if (Array.isArray(resultData)) {
                    if (resultData.length === 0) {
                        error = { code: 'PGRST116', message: 'JSON object requested, multiple (or no) rows returned' };
                        resultData = null;
                    } else if (resultData.length > 1 && this._limit !== 1) { // If limit 1 is set, single is fine
                        // Supabase actually errors if multiple rows match single()
                        // But for mock, let's just take the first if it's an insert (which returns array of inserted)
                        if (this._operation === 'insert') {
                            resultData = resultData[0];
                        } else {
                            // For select, strictly it should error, but let's be lenient or correct?
                            // Let's be correct-ish
                            resultData = resultData[0];
                        }
                    } else {
                        resultData = resultData[0];
                    }
                }
            } else if (this._maybeSingle) {
                if (Array.isArray(resultData)) {
                    if (resultData.length === 0) {
                        resultData = null;
                    } else {
                        resultData = resultData[0];
                    }
                }
            }

            resolve({ data: resultData, error, count });
        } catch (e) {
            reject(e);
        }
    }
}

export const mockSupabase = {
    from: (table: string) => new MockQueryBuilder(table),

    auth: {
        signInWithPassword: async ({ email, password }: any) => {
            if (email === 'admin@slim.com' && password === 'password123') {
                const user = {
                    id: 'admin-user-id',
                    email: 'admin@slim.com',
                    role: 'authenticated'
                };
                localStorage.setItem('mock_session', JSON.stringify({ user, access_token: 'mock-token' }));
                return { data: { user, session: { user, access_token: 'mock-token' } }, error: null };
            }
            return { data: { user: null, session: null }, error: { message: 'Invalid login credentials' } };
        },
        signOut: async () => {
            localStorage.removeItem('mock_session');
            return { error: null };
        },
        getSession: async () => {
            const sessionStr = localStorage.getItem('mock_session');
            const session = sessionStr ? JSON.parse(sessionStr) : null;
            return { data: { session }, error: null };
        },
        onAuthStateChange: (callback: any) => {
            // Simple mock, doesn't actually trigger on changes unless we implement an event bus
            return { data: { subscription: { unsubscribe: () => { } } } };
        }
    },

    storage: {
        from: (bucket: string) => ({
            upload: async (path: string, file: any) => {
                return new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        const base64 = reader.result as string;
                        try {
                            // Store the base64 string with a prefix to identify it
                            localStorage.setItem(`mock_file_${path}`, base64);
                            resolve({ data: { path }, error: null });
                        } catch (e) {
                            console.error("Mock storage quota exceeded", e);
                            resolve({ data: { path }, error: { message: "Storage quota exceeded" } });
                        }
                    };
                    reader.readAsDataURL(file);
                });
            },
            getPublicUrl: (path: string) => {
                const base64 = localStorage.getItem(`mock_file_${path}`);
                if (base64) {
                    return { data: { publicUrl: base64 } };
                }
                return { data: { publicUrl: `https://mock-storage/${path}` } };
            }
        })
    },

    functions: {
        invoke: async (functionName: string, options?: any) => {
            console.log(`Mock invoking function: ${functionName}`, options);
            if (functionName === 'get-active-pixel') {
                return { data: { pixel_id: 'mock-pixel-id' }, error: null };
            }
            return { data: {}, error: null };
        }
    }
};
